import { task, Unit } from 'true-myth';

export interface Reporter {
  count?: (name: string, amount: number) => Promise<void>;
  gauges?: (name: string, amount: number) => Promise<void>;
  measurementWithGauges?: (name: string, amount: number) => Promise<void>;
  error?: (args: { error: Error }) => Promise<void>;
}

export function defineReporter(reporter: Reporter) {
  return reporter;
}

export interface Count {
  name: string;
  amount?: number;
}

export interface Measure {
  name: string;
  amount: number;
}

// Note: ignore rejections is meaningful as we have code that listens for unhandleded rejections & produces metrics

export class Monitor {
  constructor(protected readonly reporters: Array<Reporter> = []) {}

  count(...counts: Array<Count>) {
    return task.all(
      counts.flatMap(({ name, amount = 1 }) =>
        this.reporters.map((reporter) =>
          task
            .tryOrElse(
              (reason) => `reporter failed: ${reason}`,
              async () => reporter.count?.(name, amount),
            )
            .map(() => Unit),
        ),
      ),
    );
  }

  gauges(...measures: Array<Measure>) {
    return task.all(
      measures.flatMap(({ name, amount }) =>
        this.reporters.map((reporter) =>
          task
            .tryOrElse(
              (reason) => `reporter failed: ${reason}`,
              async () => reporter.gauges?.(name, amount),
            )
            .map(() => Unit),
        ),
      ),
    );
  }

  measurementWithGauges(...measures: Array<Measure>) {
    return task.all(
      measures.flatMap(({ name, amount }) =>
        this.reporters.map((reporter) =>
          task
            .tryOrElse(
              (reason) => `reporter failed: ${reason}`,
              async () => reporter.measurementWithGauges?.(name, amount),
            )
            .map(() => Unit),
        ),
      ),
    );
  }

  error(arg: { error: Error } & Record<string, unknown>) {
    return task.all(
      this.reporters.map((reporter) =>
        task
          .tryOrElse(
            (reason) => `reporter failed: ${reason}`,
            async () => reporter.error?.(arg),
          )
          .map(() => Unit),
      ),
    );
  }
}
